import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';

interface AddClassModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { className: string; courseName: string; instructor: string; teacherPhoto?: string }) => void;
  title?: string;
}

export const AddClassModal: React.FC<AddClassModalProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit,
  title = "Add New Class"
}) => {
  const [formData, setFormData] = useState({
    className: '',
    courseName: '',
    instructor: '',
    teacherPhoto: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  if (!isOpen) return null;

  const handleTeacherPhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setErrors({ ...errors, teacherPhoto: 'Photo must be less than 2MB' });
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors({ ...errors, teacherPhoto: 'Please upload an image file' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Resize to 200x200
        const size = 200;
        canvas.width = size;
        canvas.height = size;

        // Calculate crop dimensions to maintain aspect ratio
        const minDim = Math.min(img.width, img.height);
        const sx = (img.width - minDim) / 2;
        const sy = (img.height - minDim) / 2;

        ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, size, size);

        const resizedBase64 = canvas.toDataURL('image/jpeg', 0.8);
        setFormData({ ...formData, teacherPhoto: resizedBase64 });
        setErrors({ ...errors, teacherPhoto: '' });
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    const newErrors: Record<string, string> = {};
    if (!formData.className.trim()) newErrors.className = 'Class name is required';
    if (!formData.courseName.trim()) newErrors.courseName = 'Course name is required';
    if (!formData.instructor.trim()) newErrors.instructor = 'Instructor name is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    onSubmit(formData);
    setFormData({ className: '', courseName: '', instructor: '', teacherPhoto: '' });
    setErrors({});
    onClose();
  };

  const handleClose = () => {
    setFormData({ className: '', courseName: '', instructor: '', teacherPhoto: '' });
    setErrors({});
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl border-2 border-emerald-500/60 shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 via-teal-500 to-emerald-600 p-4 flex items-center justify-between border-b-2 border-emerald-700 rounded-t-2xl">
          <div className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-white" />
            <h3 className="text-white font-black text-lg tracking-wider">{title}</h3>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:bg-emerald-600/50 h-8 w-8 rounded-lg border border-emerald-300/50 flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Class Name */}
          <div>
            <label className="text-emerald-300 text-sm font-bold mb-2 block">
              Class Name *
            </label>
            <input
              type="text"
              value={formData.className}
              onChange={(e) => setFormData({ ...formData, className: e.target.value })}
              placeholder="e.g., SOC 402"
              className="w-full bg-slate-800/80 border-2 border-emerald-500/50 text-white font-semibold rounded-lg px-4 py-3 text-base focus:outline-none focus:border-emerald-400 transition-colors"
            />
            {errors.className && (
              <p className="text-red-400 text-xs mt-1 font-medium">{errors.className}</p>
            )}
          </div>

          {/* Course Name */}
          <div>
            <label className="text-emerald-300 text-sm font-bold mb-2 block">
              Course Name *
            </label>
            <input
              type="text"
              value={formData.courseName}
              onChange={(e) => setFormData({ ...formData, courseName: e.target.value })}
              placeholder="e.g., Social Theory"
              className="w-full bg-slate-800/80 border-2 border-emerald-500/50 text-white font-semibold rounded-lg px-4 py-3 text-base focus:outline-none focus:border-emerald-400 transition-colors"
            />
            {errors.courseName && (
              <p className="text-red-400 text-xs mt-1 font-medium">{errors.courseName}</p>
            )}
          </div>

          {/* Instructor */}
          <div>
            <label className="text-emerald-300 text-sm font-bold mb-2 block">
              Instructor Name *
            </label>
            <input
              type="text"
              value={formData.instructor}
              onChange={(e) => setFormData({ ...formData, instructor: e.target.value })}
              placeholder="e.g., Dr. Smith"
              className="w-full bg-slate-800/80 border-2 border-emerald-500/50 text-white font-semibold rounded-lg px-4 py-3 text-base focus:outline-none focus:border-emerald-400 transition-colors"
            />
            {errors.instructor && (
              <p className="text-red-400 text-xs mt-1 font-medium">{errors.instructor}</p>
            )}
          </div>

          {/* Teacher Photo */}
          <div>
            <label className="text-emerald-300 text-sm font-bold mb-2 block">
              Teacher Photo (Optional)
            </label>
            <div className="flex items-center gap-3">
              {formData.teacherPhoto ? (
                <img 
                  src={formData.teacherPhoto} 
                  alt="Teacher" 
                  className="w-16 h-16 rounded-full object-cover border-2 border-emerald-400"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-400/50 flex items-center justify-center text-2xl">
                  👤
                </div>
              )}
              <label className="flex-1 px-4 py-2 rounded-lg bg-emerald-600/30 border border-emerald-400/30 text-emerald-200 text-sm font-medium hover:bg-emerald-600/40 cursor-pointer text-center transition-all">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleTeacherPhotoUpload}
                  className="hidden"
                />
                {formData.teacherPhoto ? 'Change Photo' : 'Upload Photo'}
              </label>
            </div>
            {errors.teacherPhoto && (
              <p className="text-red-400 text-xs mt-1 font-medium">{errors.teacherPhoto}</p>
            )}
            <p className="text-xs text-slate-400 mt-2">Max 2MB • Will be resized to 200×200px</p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-3 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-bold text-base border-2 border-slate-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-base border-2 border-emerald-400 transition-colors shadow-lg"
            >
              Add Class
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
